/**
 * Module dependencies.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Title } from 'app/components/atoms/title/title';
import { Spinner } from 'app/components/ui/spinner';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'app/components/ui/dialog';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { cn } from 'app/lib/utils';
import { Check, Plus, X } from 'lucide-react';
import {
  useCryptoGestRates,
  useCryptoGestSettings,
  useUpdateCryptoGestSettings
} from 'app/hooks/requests/crypto-gest/use-crypto-gest';

/**
 * The currency's human name (e.g. "British Pound"), best-effort.
 */

function currencyName(code: string): string {
  try {
    return new Intl.DisplayNames(['en'], { type: 'currency' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * The currency's symbol (e.g. "£"), best-effort.
 */

function currencySymbol(code: string): string {
  try {
    const parts = new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).formatToParts(1);

    return parts.find(part => part.type === 'currency')?.value ?? code;
  } catch {
    return code;
  }
}

/**
 * Export `CryptoGestSettings` component.
 */

export function CryptoGestSettings(): JSX.Element {
  const [translate] = useTranslation();
  const { success, error } = useToast();
  const { data: settings, isLoading } = useCryptoGestSettings();
  const { data: rates } = useCryptoGestRates();
  const update = useUpdateCryptoGestSettings();
  const [managing, setManaging] = useState(false);

  const currencies = settings?.currencies ?? ['EUR', 'USD'];

  // Every currency we have an FX rate for (plus the always-present essentials).
  const available = useMemo(() => {
    const codes = new Set<string>(['USD', 'EUR', ...Object.keys(rates?.rates ?? {})]);

    return Array.from(codes).sort();
  }, [rates]);

  const onSelect = async (displayCurrency: string) => {
    if (displayCurrency === settings?.displayCurrency) {
      return;
    }

    try {
      await update.mutateAsync({ displayCurrency });
      success({ message: translate('cryptoGest.settings.saved') });
    } catch {
      error({ message: translate('common.errors.network.default.title') });
    }
  };

  // Add/remove a currency from the quick-picker list. Removing the one in use
  // falls back to another kept currency.
  const toggle = async (code: string) => {
    const has = currencies.includes(code);
    const next = has ? currencies.filter(item => item !== code) : [...currencies, code];

    if (next.length === 0) {
      return;
    }

    const payload: { currencies: string[]; displayCurrency?: string } = { currencies: next };

    if (has && settings?.displayCurrency === code) {
      payload.displayCurrency = next[0];
    }

    try {
      await update.mutateAsync(payload);
    } catch {
      error({ message: translate('common.errors.network.default.title') });
    }
  };

  return (
    <div className={'w-full space-y-5'}>
      <div>
        <Title>{translate('cryptoGest.settings.title')}</Title>

        <p className={'-mt-2 text-sm text-muted-foreground'}>{translate('cryptoGest.settings.description')}</p>
      </div>

      <div className={'max-w-xl rounded-xl border border-border bg-card p-5 shadow-sm'}>
        <div className={'flex items-start justify-between gap-3'}>
          <div>
            <div className={'text-sm font-semibold text-foreground'}>
              {translate('cryptoGest.settings.displayCurrency')}
            </div>

            <p className={'mt-0.5 text-sm text-muted-foreground'}>
              {translate('cryptoGest.settings.displayCurrencyHint')}
            </p>
          </div>

          <Button onClick={() => setManaging(true)} size={'sm'} variant={'outline'}>
            <Plus className={'h-4 w-4'} />

            {translate('cryptoGest.settings.manageCurrencies')}
          </Button>
        </div>

        {isLoading ? (
          <div className={'py-6'}>
            <Spinner />
          </div>
        ) : (
          <div className={'mt-3 grid grid-cols-2 gap-3'}>
            {currencies.map(code => {
              const active = settings?.displayCurrency === code;

              return (
                <button
                  className={cn(
                    'flex items-center gap-3 rounded-lg border p-3 text-left transition-colors',
                    active ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'
                  )}
                  disabled={update.isLoading}
                  key={code}
                  onClick={() => onSelect(code)}
                  type={'button'}
                >
                  <span
                    className={cn(
                      'grid h-9 w-9 shrink-0 place-items-center rounded-lg text-lg font-semibold',
                      active ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {currencySymbol(code)}
                  </span>

                  <span className={'min-w-0'}>
                    <span className={'block font-medium text-foreground'}>{code}</span>

                    <span className={'block truncate text-xs text-muted-foreground'}>{currencyName(code)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <ManageCurrenciesDialog
        available={available}
        onClose={() => setManaging(false)}
        onToggle={toggle}
        open={managing}
        selected={currencies}
      />
    </div>
  );
}

/**
 * A modal to add/remove currencies from the quick-picker list.
 */

function ManageCurrenciesDialog({
  available,
  selected,
  open,
  onClose,
  onToggle
}: {
  available: string[];
  selected: string[];
  open: boolean;
  onClose: () => void;
  onToggle: (code: string) => void;
}) {
  const [translate] = useTranslation();
  const [query, setQuery] = useState('');
  const [onlySelected, setOnlySelected] = useState(false);

  const selectedSet = new Set(selected);
  const term = query.trim().toLowerCase();
  const filtered = available.filter(code => {
    if (onlySelected && !selectedSet.has(code)) {
      return false;
    }

    return term ? code.toLowerCase().includes(term) || currencyName(code).toLowerCase().includes(term) : true;
  });

  return (
    <Dialog onOpenChange={value => (value ? undefined : onClose())} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{translate('cryptoGest.settings.manageCurrencies')}</DialogTitle>
        </DialogHeader>

        {/* Search + a filter to view all currencies or only the selected ones. */}
        <div>
          <div className={'flex items-center gap-2'}>
            <Input
              className={'flex-1'}
              onChange={event => setQuery(event.target.value)}
              placeholder={translate('cryptoGest.settings.searchCurrency')}
              value={query}
            />

            <div className={'flex shrink-0 rounded-md border border-input p-0.5'}>
              {[
                { key: false, label: translate('cryptoGest.settings.currencyAll') },
                { key: true, label: translate('cryptoGest.settings.currencySelected') }
              ].map(option => (
                <button
                  className={cn(
                    'rounded px-2.5 py-1 text-xs font-medium transition-colors',
                    onlySelected === option.key
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                  key={String(option.key)}
                  onClick={() => setOnlySelected(option.key)}
                  type={'button'}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className={'-mr-2 mt-1 max-h-[55vh] divide-y divide-border overflow-y-auto pr-3 pt-3'}>
            {filtered.length === 0 && (
              <p className={'py-6 text-center text-sm text-muted-foreground'}>
                {translate('cryptoGest.settings.noCurrencies')}
              </p>
            )}

            {filtered.map(code => {
              const isSelected = selected.includes(code);

              return (
                <button
                  className={
                    'flex w-full items-center justify-between gap-3 py-2.5 text-left text-sm hover:bg-muted/40'
                  }
                  key={code}
                  onClick={() => onToggle(code)}
                  type={'button'}
                >
                  <span className={'flex min-w-0 items-center gap-3'}>
                    <span
                      className={'grid h-7 w-7 shrink-0 place-items-center rounded-md bg-muted text-sm font-semibold'}
                    >
                      {currencySymbol(code)}
                    </span>

                    <span className={'min-w-0'}>
                      <span className={'block font-medium text-foreground'}>{code}</span>

                      <span className={'block truncate text-xs text-muted-foreground'}>{currencyName(code)}</span>
                    </span>
                  </span>

                  <span
                    className={cn(
                      'grid h-6 w-6 shrink-0 place-items-center rounded-full border transition-colors',
                      isSelected
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border text-transparent'
                    )}
                  >
                    {isSelected ? <Check className={'h-3.5 w-3.5'} /> : <X className={'h-3.5 w-3.5'} />}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
